import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import React, { useContext, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { SettingsContextProps } from '../../Contexts/SettingsContext';
import SettingsContext from '../../Contexts/SettingsContext';
import AppTheme from '../../Enums/AppTheme';
import Divider from '@mui/material/Divider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faCircleInfo, faLightbulb, faRightFromBracket, faUser } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@mui/material/IconButton';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import { handleLogout } from '../../Utils/HandleLogout';
import type { ModeContextProps } from '../../Contexts/ModeContext';
import ModeContext from '../../Contexts/ModeContext';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import AboutSubTab from './AboutSubTab';
import { navBarHeight } from '../../Constants';
import { updateLastActivity } from '../../Utils/LastActivity';
import BugReportDialog from '../../Dialogs/GeneralDialogs/BugReportDialog';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { SuccessSnackbar } from 'shouldve-been-mui';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import type { RunContextProps } from '../../Contexts/RunContext';
import RunContext from '../../Contexts/RunContext';

type SettingsTabProps = {
    openedSubTab: string | null,
};

function SettingsTab(props: Readonly<SettingsTabProps>): JSX.Element {

    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const runContext: RunContextProps = useContext<RunContextProps>(RunContext);

    const navigate: NavigateFunction = useNavigate();

    const currentTheme: Theme = useTheme();

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [successSnackbarMessage, setSuccessSnackbarMessage] = useState<string | null>(null);
    const [showBugReportDialog, setShowBugReportDialog] = useState<boolean>(false);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        if (props.openedSubTab === null) {
            runContext.setTitle('K-Fleet Vessel Manager - Settings');
        }
    }, [props.openedSubTab, runContext]);

    function updateTheme(theme: AppTheme): void {

        settingsContext.setTheme(theme);
        localStorageContext.localStorageService?.setTheme(theme);
        updateLastActivity(userContext, localStorageContext);
    }

    return (
        <>
            {props.openedSubTab === null &&
                <div style={{
                    padding: '5px',
                    height: windowSize[1] - navBarHeight,
                    overflow: 'auto',
                }}>
                    <List>
                        <ListItem
                            disablePadding
                            secondaryAction={
                                <IconButton
                                    onClick={(): void => handleLogout(systemContext, tokenContext, userContext, modeContext, localStorageContext)}
                                    edge='end'
                                >
                                    <FontAwesomeIcon icon={faRightFromBracket} />
                                </IconButton>
                            }    
                        >
                            <ListItemButton disableRipple>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faUser} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={userContext.currentUser?.email ?? 'ERROR'}
                                    style={{
                                        color: currentTheme.palette.text.primary,  
                                    }}  
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                    <Divider />
                    <List>
                        <ListItem
                            disablePadding
                            secondaryAction={
                                <Switch
                                    edge='end'
                                    checked={settingsContext.theme === AppTheme.DARK_MODE}
                                    onChange={(e: React.ChangeEvent): void => updateTheme((e.target as HTMLInputElement).checked ? AppTheme.DARK_MODE : AppTheme.LIGHT_MODE)}
                                    color='primary'
                                />
                            }    
                            style={{
                                marginBottom: '10px',
                            }}
                        >
                            <ListItemButton onClick={(): void => updateTheme(settingsContext.theme === AppTheme.DARK_MODE ? AppTheme.LIGHT_MODE : AppTheme.DARK_MODE)}>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faLightbulb} />
                                </ListItemIcon>
                                <ListItemText
                                    primary='Dark Mode'
                                    style={{
                                        color: currentTheme.palette.text.primary,  
                                    }}    
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem
                            disablePadding
                            style={{
                                marginBottom: '10px',
                            }}
                        >
                            <ListItemButton onClick={(): void => navigate('/settings/about')}>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </ListItemIcon>
                                <ListItemText
                                    primary='About'
                                    style={{
                                        color: currentTheme.palette.text.primary,  
                                    }}    
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem
                            disablePadding  
                            style={{
                                marginBottom: '10px',
                            }}
                        >
                            <ListItemButton onClick={(): void => setShowBugReportDialog(true)}>
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faBug} />
                                </ListItemIcon>
                                <ListItemText
                                    primary='Send Bug Report'
                                    style={{
                                        color: currentTheme.palette.text.primary,  
                                    }}    
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>

                    <SuccessSnackbar 
                        message={successSnackbarMessage}
                        setMessage={setSuccessSnackbarMessage}
                    />

                    <BugReportDialog
                        controller={showBugReportDialog}
                        onClose={(): void => setShowBugReportDialog(false)}
                    />
                </div>
            }
            {props.openedSubTab === 'about' &&
                <AboutSubTab 
                    closeSubTab={(): void => navigate('/settings')}
                />
            }
        </>
    );
}

export default SettingsTab;